import { detectEdgeCases } from '@gpa-scales/shared-logic/engine/edgeCaseDetection';
import { useAppStore } from '@/store/useAppStore';
import { selectNonResidentHonorsPenalty } from '@/store/selectors/nonResidentHonorsPenalty';
import { useState } from 'react';
import { XIcon as X } from './ui/Icons';
import { ValidationSeverity } from '@gpa-scales/shared-types';

export function EdgeCasePanel() {
  const courses = useAppStore((s) => s.courses);
  const settings = useAppStore((s) => s.settings);
  const honorsPenalty = useAppStore(selectNonResidentHonorsPenalty);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(true);

  const edgeCases = detectEdgeCases(courses, settings).filter((ec) => !dismissed.has(ec.id));

  if (edgeCases.length === 0 && !honorsPenalty) return null;

  const severityStyles: Record<string, string> = {
    [ValidationSeverity.Error]: 'border-error bg-error-light',
    [ValidationSeverity.Warning]: 'border-warning bg-warning-light',
    [ValidationSeverity.Info]: 'border-info bg-info-light',
  };

  return (
    <div className="rounded-lg border border-border bg-surface">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <h3 className="text-sm font-semibold text-[var(--color-text)]">
          Items to Review ({edgeCases.length + (honorsPenalty ? 1 : 0)})
        </h3>
        <svg
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="space-y-2 px-4 pb-4">
          {/* FE-E6 — Non-Resident Honors Penalty Quantifier */}
          {honorsPenalty && (
            <div
              className="rounded-md border border-info bg-info-light p-3"
              data-testid="non-resident-honors-penalty"
            >
              <p className="text-xs font-medium text-[var(--color-text)]">
                As a non-resident, your {honorsPenalty.flaggedCount} honors or Dual Enrollment course(s) don&rsquo;t earn UC bonus points. Your UC Capped GPA is about {honorsPenalty.penalty.toFixed(2)} lower than a California resident with the same schedule.
              </p>
            </div>
          )}
          {edgeCases.map((ec) => (
            <div
              key={ec.id}
              className={`rounded-md border p-3 ${severityStyles[ec.severity] ?? 'border-border bg-surface'}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-[var(--color-text)]">{ec.message}</p>
                  {ec.suggestion && (
                    <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{ec.suggestion}</p>
                  )}
                </div>
                <button
                  onClick={() => setDismissed((prev) => new Set([...prev, ec.id]))}
                  className="ml-2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
